import { Check, Copy01, Key01 } from '@untitledui/icons';
import { useCallback, useId } from 'react';

import { useClipboard } from '@/hooks/useClipboard';
import { Button } from '@/lib/base/buttons/button';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';

export interface CrmWebhookSecretData {
  slug: string;
  webhook_url: string;
  bearer_token: string;
  webhook_entity_id: string;
  secret_hint: string;
  automation_url: string;
}

export interface CrmWebhookSecretModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: CrmWebhookSecretData | null;
}

function CopyAnnouncement({ copied }: { copied: string | boolean }) {
  let message = '';
  if (copied === 'url') {
    message = 'Webhook URL copied to clipboard';
  } else if (copied === 'token') {
    message = 'Token copied to clipboard';
  }
  return (
    <span className='sr-only' aria-live='polite' aria-atomic='true'>
      {message}
    </span>
  );
}

interface SecretFieldProps {
  label: string;
  labelId: string;
  value: string;
  copyId: 'url' | 'token';
  isCopied: boolean;
  onCopy: () => void;
}

function SecretField({
  label,
  labelId,
  value,
  copyId,
  isCopied,
  onCopy,
}: SecretFieldProps) {
  const buttonAriaLabel = isCopied ? `${label} copied` : `Copy ${label}`;
  return (
    <div>
      <label
        id={labelId}
        className='block text-xs font-medium text-[var(--color-text-secondary)] mb-1'
      >
        {label}
      </label>
      <div className='flex items-center gap-2'>
        <code
          aria-labelledby={labelId}
          tabIndex={0}
          data-component='copy-field'
          data-instance={copyId}
          className='flex-1 bg-[var(--color-bg-secondary)] rounded-lg px-3 py-2 font-mono text-xs text-[var(--color-text-primary)] border border-[var(--color-border-secondary)] overflow-x-auto focus-visible:outline-2 focus-visible:outline-[var(--color-brand-600)]'
        >
          {value}
        </code>
        <Button
          color='secondary'
          size='sm'
          iconLeading={isCopied ? Check : Copy01}
          onClick={onCopy}
          aria-label={buttonAriaLabel}
        >
          {isCopied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

export function CrmWebhookSecretModal({
  isOpen,
  onClose,
  data,
}: CrmWebhookSecretModalProps) {
  const { copied, copy } = useClipboard();
  const urlLabelId = useId();
  const tokenLabelId = useId();

  const handleCopyUrl = useCallback(() => {
    if (data) {
      void copy(data.webhook_url, 'url');
    }
  }, [copy, data]);

  const handleCopyToken = useCallback(() => {
    if (data) {
      void copy(data.bearer_token, 'token');
    }
  }, [copy, data]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose]
  );

  // Guard AFTER all hooks. data===null while modal is animating closed
  // is normal — render nothing until data is present.
  if (!isOpen || !data) {
    return null;
  }

  return (
    <ModalOverlay
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      isDismissable={false}
      isKeyboardDismissDisabled
    >
      <Modal className='max-w-lg bg-bg-primary'>
        <Dialog
          title='Save your CRM ingestion webhook credentials'
          description="This token is shown once. You won't be able to see it again."
          headerIcon={<Key01 className='size-6 text-fg-brand-primary' />}
          hideCloseButton
          footer={
            <div className='flex justify-end'>
              <Button color='primary' onClick={onClose}>
                Done
              </Button>
            </div>
          }
        >
          <div className='flex flex-col gap-4'>
            <SecretField
              label='Webhook URL'
              labelId={urlLabelId}
              value={data.webhook_url}
              copyId='url'
              isCopied={copied === 'url'}
              onCopy={handleCopyUrl}
            />
            <SecretField
              label='Token'
              labelId={tokenLabelId}
              value={data.bearer_token}
              copyId='token'
              isCopied={copied === 'token'}
              onCopy={handleCopyToken}
            />
            {/* Polite live region for screen-reader copy announcements */}
            <CopyAnnouncement copied={copied} />
          </div>
        </Dialog>
      </Modal>
    </ModalOverlay>
  );
}
